import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  CampaignEvents,
  CampaignPlanCreatedEvent,
  CampaignLaunchedEvent,
} from '@dreamai/types';
import api from '../../../api';
import { usePusher } from '../../../contexts/PusherContext';
import CampaignPlanComponent from './dsl-components/CampaignPlanComponent';
import CampaignOverview from './dsl-components/CampaignOverview';
import CampaignMessaging from './dsl-components/CampaignMessaging';
import CampaignTargets from './dsl-components/CampaignTargets';
import CampaignSettings from './dsl-components/CampaignSettings';
import { AppTab } from './appLayoutConfig';

interface CampaignWrapperProps {
  campaignId: string;
  threadId?: string;
  tabId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
  onTabsReady?: (tabs: AppTab[]) => void;
}

interface CampaignData {
  draft: boolean;
  campaignPlanId: string | null;
}

/** Tabs to show for draft campaigns with a plan */
export const DRAFT_CAMPAIGN_TABS: AppTab[] = [
  { id: 'campaign-plan', label: 'Campaign plan' },
];

/** Tabs to show for launched campaigns */
export const CAMPAIGN_TABS: AppTab[] = [
  { id: 'overview', label: 'Campaign overview' },
  { id: 'messaging', label: 'Messaging' },
  { id: 'targets', label: 'Target companies' },
  { id: 'settings', label: 'Settings' },
];

/**
 * Wrapper component that fetches campaign data and renders content.
 * Calls onTabsReady to tell CanvasAssets which tabs to show in the header.
 * - Draft campaigns: empty tabs, shows CampaignPlanComponent
 * - Launched campaigns: CAMPAIGN_TABS, shows tab content
 */
const CampaignWrapper: React.FC<CampaignWrapperProps> = ({
  campaignId,
  threadId,
  tabId = 'overview',
  updateThreadViewVisualDescription,
  onTabsReady,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherChannel = usePusher();
  const [campaignData, setCampaignData] = useState<CampaignData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastTabsRef = useRef<string>('');

  const fetchCampaign = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.get(`/campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setCampaignData({
          draft: response.data.campaign.draft === true,
          campaignPlanId: response.data.campaign.campaignPlanId || null,
        });
      } else {
        setError('Failed to load campaign');
      }
    } catch (err) {
      console.error('Error fetching campaign:', err);
      setError('Failed to load campaign');
    } finally {
      setLoading(false);
    }
  }, [campaignId, getAccessTokenSilently]);

  // Reset state when campaignId changes
  useEffect(() => {
    setLoading(true);
    setCampaignData(null);
    lastTabsRef.current = ''; // Reset so onTabsReady will be called for new campaign
    fetchCampaign();
  }, [campaignId, fetchCampaign]);

  // Notify parent of tabs when campaign data changes
  useEffect(() => {
    if (!campaignData || !onTabsReady) return;

    let tabs: AppTab[] = [];
    if (campaignData.draft && campaignData.campaignPlanId) {
      tabs = DRAFT_CAMPAIGN_TABS;
    } else if (!campaignData.draft) {
      tabs = CAMPAIGN_TABS;
    }
    // Draft without plan = empty tabs

    const tabsKey = JSON.stringify(tabs);

    // Only call if tabs actually changed
    if (tabsKey !== lastTabsRef.current) {
      lastTabsRef.current = tabsKey;
      onTabsReady(tabs);
    }
  }, [campaignData, onTabsReady]);

  // Listen for campaign updates via Pusher
  useEffect(() => {
    if (!pusherChannel) return;

    const handleCampaignPlanCreated = (data: CampaignPlanCreatedEvent) => {
      if (String(data.campaignId) === String(campaignId)) {
        fetchCampaign();
      }
    };

    const handleCampaignLaunched = (data: CampaignLaunchedEvent) => {
      console.log(
        'CAMPAIGN_LAUNCHED received:',
        data,
        'my campaignId:',
        campaignId,
      );
      if (String(data.campaignId) === String(campaignId)) {
        console.log('Campaign ID matches, refetching...');
        fetchCampaign();
      }
    };

    pusherChannel.bind(
      CampaignEvents.CAMPAIGN_PLAN_CREATED,
      handleCampaignPlanCreated,
    );
    pusherChannel.bind(
      CampaignEvents.CAMPAIGN_LAUNCHED,
      handleCampaignLaunched,
    );

    return () => {
      pusherChannel.unbind(
        CampaignEvents.CAMPAIGN_PLAN_CREATED,
        handleCampaignPlanCreated,
      );
      pusherChannel.unbind(
        CampaignEvents.CAMPAIGN_LAUNCHED,
        handleCampaignLaunched,
      );
    };
  }, [pusherChannel, campaignId, fetchCampaign]);

  if (loading) {
    return <div className='campaign-wrapper loading'></div>;
  }

  if (error || !campaignData) {
    return null;
  }

  // Draft campaign with campaign plan: show Campaign Plan
  if (campaignData.draft && campaignData.campaignPlanId) {
    return (
      <CampaignPlanComponent
        campaignPlanId={campaignData.campaignPlanId}
        threadId={threadId}
        updateThreadViewVisualDescription={updateThreadViewVisualDescription}
      />
    );
  }

  // Draft campaign without campaign plan yet: show waiting state
  if (campaignData.draft && !campaignData.campaignPlanId) {
    return <div className='campaign-wrapper waiting'></div>;
  }

  // Launched campaign: render tab content (tabs shown in CanvasAssets header)
  switch (tabId) {
    case 'overview':
      return (
        <CampaignOverview
          campaignId={campaignId}
          threadId={threadId}
          updateThreadViewVisualDescription={updateThreadViewVisualDescription}
        />
      );
    case 'messaging':
      return <CampaignMessaging campaignId={campaignId} threadId={threadId} />;
    case 'targets':
      return <CampaignTargets campaignId={campaignId} threadId={threadId} />;
    case 'settings':
      return <CampaignSettings campaignId={campaignId} threadId={threadId} />;
    default:
      return (
        <CampaignOverview
          campaignId={campaignId}
          threadId={threadId}
          updateThreadViewVisualDescription={updateThreadViewVisualDescription}
        />
      );
  }
};

export default CampaignWrapper;
